import type { Bounds } from '@manudev/assistant';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import {
  buildBatches,
  elementBox,
  presetKey,
  rebatchStoreys,
  type BatchOptions,
} from '@/render/batch';
import { batchPointer, hoverElement } from '@/render/pick';
import { rigLayer, type LayerRig } from '@/render/rig';
import type { ModelAsset } from '@/content/schema';
import { useSelectionStore } from '@/state/selection';
import { useHeroStore } from '@/store/store';
import type { LayerHandle } from './GlbLayer';
import { storeSource } from './sources';

// Headroom per batch so the next build usually refills in place instead of reallocating.
const STORE_BATCHES: BatchOptions = { keyOf: presetKey, spare: 0.25 };
// The rig's plane starts at y = 0, which hides anything built above ground; the layer starts
// open and the next station reset clips it with the others.
const STORE_UNCLIPPED = 1e4;
export const STORE_LAYER = 'store';
const STORE_MODEL: ModelAsset = {
  id: STORE_LAYER,
  glb: 'store.glb',
  discipline: 'arch',
  label: 'built',
};

// Elements the visitor authored into the store: a layer handle like the GLB ones, so every
// station treats them the same, mounted only once the store has something to show.
export function StoreLayer({ onLayer }: { onLayer: (handle: LayerHandle | null) => void }) {
  const newIds = useHeroStore((s) => s.newIds);
  const meshes = useHeroStore((s) => s.meshes);
  const elements = useHeroStore((s) => s.elements);
  const storeys = useHeroStore((s) => s.storeys);
  const rigRef = useRef<LayerRig | null>(null);
  const layerRef = useRef<ReturnType<typeof buildBatches> | null>(null);
  const container = useMemo(() => new THREE.Group(), []);

  useEffect(() => {
    if (meshes.length === 0 && !layerRef.current) return;
    const tree = storeSource(meshes, elements, storeys).object;
    const local = new THREE.Box3().setFromObject(tree);
    if (!layerRef.current || !rigRef.current) {
      const layer = buildBatches(tree, STORE_BATCHES);
      layerRef.current = layer;
      rigRef.current = rigLayer(layer, 'shell', false);
      rigRef.current.setClipY(STORE_UNCLIPPED);
      container.add(layer.root);
    } else {
      rebatchStoreys(layerRef.current, tree, STORE_BATCHES);
      rigRef.current.refresh();
    }
    onLayer({ model: STORE_MODEL, rig: rigRef.current, box: local });
    const box = new THREE.Box3();
    const bounds: Record<number, Bounds> = {};
    for (const el of rigRef.current.elements.values()) {
      if (elementBox(el, box).isEmpty()) continue;
      bounds[el.expressID] = [box.min.x, box.min.y, box.min.z, box.max.x, box.max.y, box.max.z];
    }
    const selection = useSelectionStore.getState();
    selection.setBounds({ ...(selection.bounds ?? {}), ...bounds });
  }, [meshes, elements, storeys, container, onLayer]);

  useEffect(
    () => () => {
      hoverElement(null);
      onLayer(null);
      rigRef.current?.dispose();
      rigRef.current = null;
      layerRef.current = null;
    },
    [onLayer],
  );

  const handlers = useMemo(
    () =>
      batchPointer('store', () => {
        const pick = useSelectionStore.getState().pick;
        return pick === 'all' || pick === 'arch';
      }),
    [],
  );

  if (newIds.length === 0) return null;

  return <primitive object={container} {...handlers} />;
}
